/**
 * CREATIVE DETONATOR - Export Module
 * TXT and PDF export functionality for saved ideas
 *
 * Supports both V1 and V2 session exports:
 * - Full mode: Includes all prompts and responses (internal/debug)
 * - Clean mode: Ideas only without prompts (client sharing)
 *
 * v61 - Added PDF export with images and styled output
 */

// v61
import { GeneratorState, API_CONFIG } from '../core/state.js';
import { showError } from '../../../js/core/error-handler.js';
import { ENDPOINTS } from '../../../js/api/endpoints.js';
import { apiRequest } from '../../../js/api/client.js';
import { createLogger } from '../../../js/core/logger.js';

const log = createLogger('export');

// ============================================================================
// SESSION TXT EXPORT (via backend)
// ============================================================================

/**
 * Export full session to TXT via backend API
 * Uses session data stored from generation response
 * Backend handles full/clean mode based on SYSTEM_PROMPT_EXPORT env
 */
export async function exportSessionTxt() {
  const session = GeneratorState.session;

  if (!session) {
    showError('No session data available. Generate ideas first.', 'exporting session');
    return;
  }

  try {
    const response = await apiRequest(API_CONFIG.endpoints.exportSession, {
      method: 'POST',
      body: JSON.stringify({ session })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || 'Export failed');
    }

    // Get the TXT content as blob
    const blob = await response.blob();

    // Download
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `creative-session-${Date.now()}.txt`;
    a.click();
    URL.revokeObjectURL(url);

  } catch (error) {
    console.error('Session export error:', error);
    showError(`Export failed: ${error.message}`, 'exporting session');
  }
}

// ============================================================================
// SAVED IDEAS TXT EXPORT (client-side)
// ============================================================================

/**
 * Export saved ideas to a TXT file (client-side generation)
 * If no ideas are manually saved, exports ALL ideas from the session
 */
export function exportIdeasTxt() {
  // Collect saved ideas (or all ideas if none saved)
  const savedIdeaData = [];
  const hasSavedIdeas = GeneratorState.savedIdeas && GeneratorState.savedIdeas.length > 0;

  GeneratorState.generatedIdeas.forEach(group => {
    group.ideas.forEach(idea => {
      // If no saved ideas, export all; otherwise only export saved
      if (!hasSavedIdeas || GeneratorState.savedIdeas.includes(idea.id)) {
        savedIdeaData.push({
          trigger: `${group.trigger.code} - ${group.trigger.name}`,
          format: idea.format || 'unknown',
          title: idea.title,
          idea: idea.idea || '',
          headline: idea.headline || '',
          subline: idea.subline || '',
          text: idea.text,
          whyItWorks: idea.whyItWorks || idea.mechanism || '',
          // V2 lineage fields
          sourceFragment: idea.sourceFragment || null,
          sourceTruth: idea.sourceTruth || null
        });
      }
    });
  });

  if (savedIdeaData.length === 0) {
    showError('No ideas available. Generate ideas first.', 'exporting ideas');
    return;
  }

  // Create export text with multi-format support
  const { product, benefit, formats } = GeneratorState.brief;
  const formatDisplay = formats.map(f => f.toUpperCase()).join(' + ');
  const version = GeneratorState.promptVersion || 'v1';
  const headerLabel = hasSavedIdeas ? 'SAVED IDEAS' : 'ALL IDEAS';

  let exportText = `CREATIVE DETONATOR - ${headerLabel}\n`;
  exportText += `=====================================\n\n`;
  exportText += `Version: ${version.toUpperCase()}\n`;
  exportText += `Brief: ${product} | ${benefit} | [${formatDisplay}]\n`;
  exportText += `Generated: ${new Date().toLocaleString()}\n\n`;
  exportText += `${headerLabel} (${savedIdeaData.length}):\n`;
  exportText += `-------------------------------------\n\n`;

  savedIdeaData.forEach((idea, i) => {
    exportText += `${i + 1}. ${idea.title.toUpperCase()}\n`;
    exportText += `   Trigger: ${idea.trigger}\n`;
    exportText += `   Format: ${idea.format.toUpperCase()}\n`;

    // V2 lineage (if present)
    if (idea.sourceFragment) {
      exportText += `\n   SPARKED BY:\n   "${idea.sourceFragment}"\n`;
    }
    if (idea.sourceTruth) {
      exportText += `\n   THE TRUTH:\n   ${idea.sourceTruth}\n`;
    }

    if (idea.idea) {
      exportText += `\n   THE IDEA:\n   ${idea.idea}\n`;
    }
    if (idea.headline) {
      exportText += `\n   HEADLINE: "${idea.headline}"\n`;
    }
    if (idea.subline) {
      exportText += `   SUBLINE: ${idea.subline}\n`;
    }
    exportText += `\n   VISUAL:\n   ${idea.text}\n`;
    if (idea.whyItWorks) {
      exportText += `\n   WHY IT WORKS:\n   ${idea.whyItWorks}\n`;
    }
    exportText += `\n${'─'.repeat(50)}\n\n`;
  });

  // Download as text file
  const blob = new Blob([exportText], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `creative-detonator-saved-ideas-${Date.now()}.txt`;
  a.click();
  URL.revokeObjectURL(url);
}

// ============================================================================
// PDF EXPORT
// ============================================================================

/**
 * Export saved ideas to a PDF file (legacy - for saved ideas only)
 * Now redirects to full session PDF export
 */
export function exportIdeasPdf() {
  // Redirect to full session export
  exportSessionPdf();
}

/**
 * Export full session to PDF via backend API
 * Uses session data stored from generation response
 * Generates beautifully styled PDF with images
 *
 * Works for both:
 * - New generations (GeneratorState.session is set)
 * - Reopened sessions (reconstructs from GeneratorState data if needed)
 */
export async function exportSessionPdf() {
  // Guard against double exports
  if (isExporting) {
    log.warn('PDF export already in progress, ignoring duplicate call');
    return;
  }
  isExporting = true;

  // Try to get session, or construct from GeneratorState
  let session = GeneratorState.session;

  // DEBUG: Log preloadedImages state
  const preloadedKeys = Object.keys(GeneratorState.preloadedImages || {});
  log.info('PDF Export Debug:', {
    hasSession: !!session,
    preloadedImagesCount: preloadedKeys.length,
    preloadedImageKeys: preloadedKeys.slice(0, 5), // First 5 keys
    sessionResultsCount: session?.results?.length || 0
  });

  // If no session, construct one from available GeneratorState data
  // This handles edge cases where reopened sessions don't have full session data
  if (!session) {
    const ideas = GeneratorState.generatedIdeas || [];
    const brief = GeneratorState.brief || {};
    const triggers = GeneratorState.selectedTriggers || [];

    if (ideas.length === 0) {
      showError('No ideas available. Generate ideas first.', { context: 'exporting PDF' });
      return;
    }

    log.info('Constructing session from GeneratorState for PDF export');

    // Construct a session object that matches what the backend expects
    session = {
      version: GeneratorState.promptVersion || 'v5',
      timestamp: new Date().toISOString(),
      brief: {
        product: brief.product || '',
        benefit: brief.benefit || '',
        formats: brief.formats || []
      },
      selectedTriggers: triggers.map(t => ({
        code: t.code || t,
        name: t.name || '',
        question: t.question || ''
      })),
      // Convert generatedIdeas groups to results format
      results: ideas.map(group => ({
        triggerCode: group.trigger?.code || '',
        triggerName: group.trigger?.name || '',
        ideas: (group.ideas || []).map(idea => ({
          ...idea,
          // Ensure image URL is available in either format
          image_url: idea.image_url || idea.imageUrl || GeneratorState.preloadedImages?.[idea.id],
          imageUrl: idea.imageUrl || idea.image_url || GeneratorState.preloadedImages?.[idea.id]
        }))
      }))
    };
  } else {
    // Ensure results have image URLs from preloadedImages
    if (session.results) {
      session = {
        ...session,
        results: session.results.map(result => ({
          ...result,
          ideas: (result.ideas || []).map(idea => ({
            ...idea,
            image_url: idea.image_url || idea.imageUrl || GeneratorState.preloadedImages?.[idea.id],
            imageUrl: idea.imageUrl || idea.image_url || GeneratorState.preloadedImages?.[idea.id]
          }))
        }))
      };
    }
  }

  // DEBUG: Log merged image URLs
  if (session?.results) {
    let imagesFound = 0;
    let imagesMissing = 0;
    const sampleIds = [];
    const sampleFoundUrls = [];
    session.results.forEach(result => {
      (result.ideas || []).forEach(idea => {
        if (idea.image_url || idea.imageUrl) {
          imagesFound++;
          if (sampleFoundUrls.length < 2) {
            sampleFoundUrls.push((idea.image_url || idea.imageUrl).substring(0, 50));
          }
        } else {
          imagesMissing++;
          if (sampleIds.length < 3) {
            sampleIds.push(idea.id || 'NO_ID');
          }
        }
      });
    });
    // More detailed logging - show actual values
    console.log('[PDF Export] Session result IDs:', session.results.flatMap(r => (r.ideas || []).map(i => i.id)).slice(0, 5));
    console.log('[PDF Export] PreloadedImages keys:', preloadedKeys.slice(0, 5));
    console.log('[PDF Export] ID match test:', preloadedKeys[0], 'in session?',
      session.results.some(r => (r.ideas || []).some(i => i.id === preloadedKeys[0])));

    log.info('PDF Export - Image merge result:', {
      imagesFound,
      imagesMissing,
      sampleMissingIds: sampleIds,
      sampleFoundUrls,
      preloadedSampleKeys: preloadedKeys.slice(0, 3),
      preloadedSampleValues: preloadedKeys.slice(0, 2).map(k =>
        (GeneratorState.preloadedImages[k] || '').substring(0, 50)
      )
    });
  }

  // Get options from UI
  const includeFragments = document.getElementById('include-fragments')?.checked ?? true;

  // Show loading overlay
  showExportLoading('Generating PDF...');

  try {
    log.info('Exporting session as PDF', {
      product: session?.brief?.product,
      options: { includeFragments }
    });

    // DEBUG: Log phase data being sent
    console.log('[PDF Export] Session phase data being sent:', {
      hasPhase1: !!session?.phase1,
      hasPhase2a: !!session?.phase2a,
      hasPhase2b: !!session?.phase2b,
      phase1Keys: session?.phase1 ? Object.keys(session.phase1) : [],
      fragmentsCount: session?.phase1?.parsedResponse?.fragments?.length || 0
    });

    const response = await apiRequest(ENDPOINTS.media.exportSessionPdf, {
      method: 'POST',
      body: JSON.stringify({
        session,
        options: {
          includeFragments,
          includeImages: true,
          layout: 'cards'
        }
      })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || 'PDF generation failed');
    }

    // Get the PDF blob
    const blob = await response.blob();

    // Generate filename
    const productName = (session?.brief?.product || 'session')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .substring(0, 30);
    const timestamp = new Date().toISOString().split('T')[0];
    const filename = `creative-detonator-${productName}-${timestamp}.pdf`;

    // Download
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);

    log.info('PDF exported successfully', { filename, size: blob.size });

    // Show success toast
    if (window.showToast) {
      window.showToast('PDF downloaded successfully!', 'success');
    }

  } catch (error) {
    log.error('PDF export error:', error);
    showError(`PDF export failed: ${error.message}`, { context: 'exporting PDF' });
  } finally {
    hideExportLoading();
    isExporting = false;  // Reset export guard
  }
}

// ============================================================================
// LOADING OVERLAY
// ============================================================================

let loadingOverlay = null;
let isExporting = false;  // Guard against double exports
let dropdownInitialized = false;  // Guard against duplicate event listeners

/**
 * Show export loading overlay
 */
function showExportLoading(message = 'Exporting...') {
  // Create if doesn't exist
  if (!loadingOverlay) {
    loadingOverlay = document.createElement('div');
    loadingOverlay.className = 'export-loading';
    loadingOverlay.innerHTML = `
      <div class="export-loading-content">
        <div class="pixel-spinner"></div>
        <p class="export-message">${message}</p>
        <p class="export-progress"></p>
      </div>
    `;
    document.body.appendChild(loadingOverlay);
  } else {
    loadingOverlay.querySelector('.export-message').textContent = message;
    loadingOverlay.style.display = 'flex';
  }
}

/**
 * Hide export loading overlay
 */
function hideExportLoading() {
  if (loadingOverlay) {
    loadingOverlay.style.display = 'none';
  }
}

/**
 * Update loading progress text
 */
function updateExportProgress(text) {
  if (loadingOverlay) {
    const progress = loadingOverlay.querySelector('.export-progress');
    if (progress) {
      progress.textContent = text;
    }
  }
}

// ============================================================================
// DROPDOWN UI
// ============================================================================

/**
 * Initialize export dropdown functionality
 * Call this from phase4 initialization
 */
export function initExportDropdown() {
  // Guard against duplicate initialization (prevents duplicate event listeners)
  if (dropdownInitialized) {
    log.debug('Export dropdown already initialized');
    return;
  }

  const dropdown = document.getElementById('export-dropdown');
  const toggleBtn = document.getElementById('btn-download-session');
  const menu = document.getElementById('export-menu');
  const pdfBtn = document.getElementById('btn-export-pdf');
  const txtBtn = document.getElementById('btn-export-txt');

  if (!dropdown || !toggleBtn) {
    log.debug('Export dropdown elements not found');
    return;
  }

  dropdownInitialized = true;

  // Toggle dropdown on button click
  toggleBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    dropdown.classList.toggle('open');
  });

  // Close dropdown when clicking outside
  document.addEventListener('click', (e) => {
    if (!dropdown.contains(e.target)) {
      dropdown.classList.remove('open');
    }
  });

  // PDF export button
  if (pdfBtn) {
    pdfBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      dropdown.classList.remove('open');
      exportSessionPdf();
    });
  }

  // TXT export button
  if (txtBtn) {
    txtBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      dropdown.classList.remove('open');
      exportSessionTxt();
    });
  }

  // Prevent dropdown from closing when clicking inside menu
  if (menu) {
    menu.addEventListener('click', (e) => {
      // Only stop propagation for checkbox area
      if (e.target.closest('.export-checkbox')) {
        e.stopPropagation();
      }
    });
  }

  log.debug('Export dropdown initialized');
}

// ============================================================================
// GLOBAL EXPORTS FOR COMPATIBILITY
// ============================================================================

// Expose to window for compatibility
window.exportIdeasTxt = exportIdeasTxt;
window.exportIdeasPdf = exportIdeasPdf;
window.exportSessionTxt = exportSessionTxt;
window.exportSessionPdf = exportSessionPdf;
window.initExportDropdown = initExportDropdown;

